import { catchErrorHandler } from "@picovico/utils";
import { useCallback } from "react";
import { useLazySocialLoginInitiateQuery } from "store/services/auth";
import {
  createSocialAuthState,
  rememberSocialAuthState,
  takeSocialAuthState,
} from "utils/socialAuthState";

import { useAppToast } from "./useAppToast";

/**
 * Starts "Sign in with <provider>": asks the backend for the consent URL with a fresh `state`,
 * keeps that state in this browser for the verify page to compare, and redirects.
 */
export function useSocialSignIn() {
  const [initiate] = useLazySocialLoginInitiateQuery();
  const { error: errorToast } = useAppToast();

  return useCallback(
    (providerName: string, next?: string) => {
      const state = createSocialAuthState();
      return initiate({ idx: providerName, next, state })
        .unwrap()
        .then((res: any) => {
          const url = res?.data?.url || res?.url;
          if (!url) {
            errorToast(`Unable to initiate ${providerName} login.`);
            return;
          }
          // A backend that does not know `state` (the per-hall fallback) leaves it out of the
          // URL; keep nothing then, so its callback is not held to a state it never had.
          takeSocialAuthState();
          if (new URL(url).searchParams.get("state") === state) {
            rememberSocialAuthState(state);
          }
          window.location.href = url;
        })
        .catch((e) => {
          catchErrorHandler(e, errorToast);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initiate],
  );
}
